import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { Button } from '@domcrm/embed-ui';

import { getCallStatus, postRequestCall } from '../widget-api/auth-call';
import {
  postAuthSelectContext,
  type ContextRequiredResponse,
  type LoginContextOption
} from '../widget-api/auth-contexts';
import { getMe, postPublicResendEmailVerification } from '../widget-api/auth-me';
import { postClientLogin } from '../widget-api/client-login';
import { ApiError } from '../widget-api/http-client';
import { useEmbedBootstrap } from '../use-widget-bootstrap';
import { ensureOrgSlugInSearchSuffix } from '../widget-lib/auth-search';
import { setEmbedSession, setGlobalEmbedSession } from '../widget-lib/embed-session-store';
import { navigateAfterClientAuth } from '../widget-lib/navigate-after-client-auth';
import { clearClientSignInDraft, readClientSignInDraft, saveClientSignInDraft } from '../widget-lib/signin-draft';
import { clearPendingCall, loadPendingCall, savePendingCall } from '../widget-lib/pending-call';
import { normalizePhoneRu } from '../widget-lib/phone';
import { telHref } from '../widget-lib/phone-tel';
import { PATH } from '../widget-lib/path-routes';
import { validateEmail, validatePhoneRu } from '../widget-lib/validation';
import { fetchPublicLegalDocuments } from '../widget-api/public-legal-documents';
import { LegalAgreementHtmlBlock } from '../widget-ui/legal-agreement-html-block';
import { EmbedPhoneInput } from '../widget-ui/embed-phone-input';
import { EmbedMessageBlock, embedFieldErrorInputClass } from '../widget-ui/embed-message-block';
import { ContextSelectScreen } from '../widget-ui/context-select-screen';
import { useWidgetSeo } from '../widget-hooks/use-widget-seo';
import { PromoBanner } from '../widget-ui/promo-banner';
import { SmartCaptchaNotice } from '../widget-ui/smart-captcha-notice';
import { useSmartCaptcha } from '../widget-lib/smartcaptcha';

/** База: на узком экране чуть крупнее шрифт (читаемость + меньше авто-зума в iOS). */
const inputClass =
  'min-h-10 w-full rounded-sm border-0 bg-[var(--bg-soft)] px-3 py-2 text-base text-[var(--text)] focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)] sm:min-h-8 sm:py-1 sm:text-[13px]';

/** Кулдаун повторного запроса звонка; backend rate-limit — 3/мин, 10/час. */
const CALL_RESEND_COOLDOWN_S = 30;

/** Кулдаун повторного письма-верификации; backend rate-limit — не более 10 в час с одного IP. */
const RESEND_COOLDOWN_S = 300;

const tabBtnClass = (active: boolean) =>
  `min-h-11 rounded-sm px-2 py-2 text-[12px] sm:text-[13px] font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)] sm:min-h-0 sm:py-1.5 ${
    active ? 'bg-[var(--bg-soft)] text-[var(--text)]' : 'text-[var(--text-muted)] hover:text-[var(--text)]'
  }`;

function IconEye() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function IconEyeOff() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
      <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
      <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
      <line x1="2" x2="22" y1="2" y2="22" />
    </svg>
  );
}

// #__PLUSOFON_DISABLE__ — оригинальный код временно закомментирован, не удалён.
// /** Допустимые провайдеры callcheck из ответа; если сервер ничего не прислал — по умолчанию только sms_ru. */
// function normalizeCallcheckProviders(list: unknown): string[] {
//   if (!Array.isArray(list)) return ['sms_ru'];
//   const arr = list.filter((x): x is string => x === 'sms_ru' || x === 'plusofon');
//   return arr.length ? Array.from(new Set(arr)) : ['sms_ru'];
// }

export default function SignInPage() {
  useWidgetSeo(
    'Вход в личный кабинет',
    'Авторизация в личном кабинете для просмотра записей, покупки абонементов и онлайн-оплаты.'
  );
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const qs = searchParams.toString();
  const suffix = qs ? `?${qs}` : '';
  const embedGlobal = searchParams.get('embed_global') === '1';
  /** Мобильная оболочка (iframe внутри приложения): только там нужен черновик формы. */
  const isMobileShellEmbed = useMemo(() => {
    try {
      return window.parent !== window && searchParams.get('widget_channel') === 'mobile';
    } catch {
      return false;
    }
  }, [searchParams]);
  /** Стартовые значения полей: возврат из юридического документа сохраняет введённое. */
  const initialDraft = useMemo(
    () => (isMobileShellEmbed ? readClientSignInDraft() : null),
    [isMobileShellEmbed]
  );
  const { orgSlug, orgSlugs, bootstrapHint, guestShowcaseOrg } = useEmbedBootstrap();
  const effectiveOrgSlug = useMemo(() => {
    return orgSlug?.trim() || guestShowcaseOrg?.slug?.trim() || null;
  }, [orgSlug, guestShowcaseOrg]);
  const studioSlugs = useMemo(() => {
    if (orgSlugs.length > 0) return orgSlugs;
    return effectiveOrgSlug ? [effectiveOrgSlug] : [];
  }, [orgSlugs, effectiveOrgSlug]);
  const hasStudioScope = Boolean(effectiveOrgSlug || studioSlugs.length > 0);

  const [formError, setFormError] = useState<string | null>(null);
  const [needRegister, setNeedRegister] = useState<string | null>(null);
  // context_required: приложение с несколькими студиями — выбор студии после входа.
  const [studioChoice, setStudioChoice] = useState<ContextRequiredResponse | null>(null);
  const [studioBusyId, setStudioBusyId] = useState<string | null>(null);
  const [studioError, setStudioError] = useState<string | null>(null);

  const [offerOverlay, setOfferOverlay] = useState<{ title: string; text: string } | null>(null);

  useEffect(() => {
    const offer = searchParams.get('offer');
    if (!offer) return;
    void (async () => {
      try {
        const docs = await fetchPublicLegalDocuments(orgSlug ?? undefined);
        const titles: Record<string, string> = { privacy: 'Согласие на обработку персональных данных', crm: 'Пользовательское соглашение CRM', rules: 'Пользовательское соглашение', organization: 'Соглашение с организацией' };
        const texts: Record<string, string | undefined | null> = { privacy: docs.PersonalDataProcessingText, crm: docs.CrmUserAgreementText, rules: docs.PlatformUserRulesText, organization: docs.OrgUserAgreementText };
        if (texts[offer]) setOfferOverlay({ title: titles[offer] ?? 'Соглашение', text: texts[offer] ?? '' });
      } catch {
        /** */
      }
    })();
  }, [searchParams, orgSlug]);

  /** Режимы входа: пароль, звонок */
  const [authTab, setAuthTab] = useState<'password' | 'call'>(initialDraft?.authTab ?? 'call');
  /** #__INCOMING_CALL_DISABLE__ — только callcheck: шаги 'phone' → 'callcheck'. */
  const [callStep, setCallStep] = useState<'phone' | 'callcheck'>('phone');

  const [callPhoneInput, setCallPhoneInput] = useState(initialDraft?.callPhoneInput ?? '+7 ');
  const [checkId, setCheckId] = useState<string | null>(null);
  const [callSubmitting, setCallSubmitting] = useState(false);
  const [callCooldown, setCallCooldown] = useState(0);
  const lastPhoneNormRef = useRef<string>('');
  const [callcheckPhonePretty, setCallcheckPhonePretty] = useState(''); // номер для исходящего звонка

  // Восстанавливаем ожидание звонка после возврата из телефонного приложения/перезагрузки WebView.
  useEffect(() => {
    const pending = loadPendingCall('client', effectiveOrgSlug);
    if (!pending) return;
    setCallPhoneInput(pending.userPhone || '+7 ');
    setCallcheckPhonePretty(pending.callPhonePretty || '');
    setCallStep('callcheck');
    setCheckId(pending.checkId);
  }, [effectiveOrgSlug]);

  // #__PLUSOFON_DISABLE__: выбор оператора звонка отключён:
  // const [callProvider, setCallProvider] = useState<'sms_ru' | 'plusofon'>('sms_ru');
  // const [callcheckProviders, setCallcheckProviders] = useState<string[]>(['sms_ru']);

  /** Вход по паролю: email, телефон учётной записи и пароль (паритет с AuthScreen админки). */
  const [loginEmail, setLoginEmail] = useState(initialDraft?.loginEmail ?? '');
  const [accountPhone, setAccountPhone] = useState(initialDraft?.accountPhone ?? '+7 ');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  /** Гейт email: показываем форму повторной отправки письма-верификации. */
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [resendBusy, setResendBusy] = useState(false);
  const [resendNote, setResendNote] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; phone?: string; password?: string }>({});

  // Возврат с юридического документа не должен терять уже введённые поля (мобильная оболочка).
  useEffect(() => {
    if (!isMobileShellEmbed) return;
    saveClientSignInDraft({ authTab, callPhoneInput, loginEmail, accountPhone });
  }, [isMobileShellEmbed, accountPhone, authTab, callPhoneInput, loginEmail]);

  // Yandex SmartCaptcha (невидимая): включается бэкендом (SMARTCAPTCHA_MODE=required).
  const captcha = useSmartCaptcha();

  const canSubmitCall = useMemo(() => {
    const phoneOk = validatePhoneRu(callPhoneInput, { required: true }) === true;
    return phoneOk && !callSubmitting;
  }, [callPhoneInput, callSubmitting]);

  /** В глобальном режиме вход без org_slug — после ответа редирект на /organizations или на расписание. */
  const isGlobalLogin = embedGlobal && !effectiveOrgSlug;

  type ClientTokens = { access_token: string; refresh_token?: string | null };
  type FinishClientOpts = {
    organizationId?: number | null;
    organizationSlug?: string | null;
    needOrgSelect?: boolean;
  };

  /** Общий финиш клиентского входа: сессия, глобальный режим и переход по онбордингу. */
  const finishClientAuth = useCallback(async (tokens: ClientTokens, opts?: FinishClientOpts) => {
    clearClientSignInDraft();
    const me = opts?.organizationId == null ? await getMe(tokens.access_token) : null;
    const oid = opts?.organizationId ?? me?.organization_id ?? me?.context?.org_id ?? null;
    if (oid == null && isGlobalLogin) {
      setGlobalEmbedSession({
        accessToken: tokens.access_token,
        organizationId: null,
        embedMode: 'client',
        refreshToken: tokens.refresh_token ?? undefined
      });
      navigate(`${PATH.home}${suffix}`);
      return;
    }
    if (oid == null) {
      setFormError('Не удалось определить организацию. Обратитесь в поддержку.');
      setCallStep('phone');
      return;
    }
    const primaryMembership = me?.memberships?.find((m) => m.organization_id === oid);
    const sessionSlug =
      opts?.organizationSlug?.trim() || primaryMembership?.organization_slug?.trim() || effectiveOrgSlug || `org_${oid}`;
    const authSuffix = effectiveOrgSlug ? ensureOrgSlugInSearchSuffix(suffix, effectiveOrgSlug) : suffix;
    const finalSuffix = ensureOrgSlugInSearchSuffix(authSuffix, sessionSlug);
    setEmbedSession(sessionSlug, {
      accessToken: tokens.access_token,
      organizationId: oid,
      embedMode: 'client',
      refreshToken: tokens.refresh_token ?? undefined
    });
    if (isGlobalLogin) {
      setGlobalEmbedSession({
        accessToken: tokens.access_token,
        organizationId: oid,
        embedMode: 'client',
        refreshToken: tokens.refresh_token ?? undefined
      });
      navigate(opts?.needOrgSelect ? `${PATH.organizations}${finalSuffix}` : `${PATH.home}${finalSuffix}`);
      return;
    }
    await navigateAfterClientAuth(navigate, finalSuffix, tokens.access_token, oid);
  }, [effectiveOrgSlug, isGlobalLogin, navigate, suffix]);

  const showStudioChoice = useCallback((res: ContextRequiredResponse) => {
    setStudioChoice(res);
    setStudioBusyId(null);
    setStudioError(null);
    setFormError(null);
    setCheckId(null);
    setCallStep('phone');
  }, []);

  const onSelectStudio = useCallback(async (context: LoginContextOption) => {
    const choice = studioChoice;
    if (!choice) return;
    setStudioBusyId(context.id);
    setStudioError(null);
    try {
      const tokens = await postAuthSelectContext(choice.selection_token, context.id);
      setStudioChoice(null);
      await finishClientAuth(tokens);
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.errorType === 'client_self_join_staff_only') {
          setStudioError('Студия принимает новых клиентов только через администратора');
        } else if (e.errorType === 'selection_token_invalid' || e.status === 401) {
          setStudioError('Время выбора истекло. Войдите заново.');
          setStudioChoice(null);
        } else {
          setStudioError(e.detail);
        }
      } else {
        setStudioError(e instanceof Error ? e.message : 'Не удалось войти. Попробуйте ещё раз.');
      }
    } finally {
      setStudioBusyId(null);
    }
  }, [finishClientAuth, studioChoice]);

  useEffect(() => {
    if (!checkId) return;

    let stopped = false;
    let intervalId: ReturnType<typeof setInterval> | null = null;

    const stopPoll = () => {
      if (intervalId != null) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const finish = () => {
      stopPoll();
      setCheckId(null);
      clearPendingCall('client');
      setCallStep('phone');
    };

    const tick = async () => {
      try {
        const s = await getCallStatus(checkId);
        if (stopped) return;

        if (s.status === 'pending') {
          return;
        }

        if (s.status === 'expired') {
          finish();
          setFormError('Время попытки авторизации истекло. Запросите вход заново.');
          return;
        }

        if (s.status === 'two_factor_required') {
          finish();
          setFormError('Для этой учётной записи включена 2FA. Войдите по паролю.');
          return;
        }

        if (s.status === 'context_required') {
          stopPoll();
          setCheckId(null);
          clearPendingCall('client');
          setCallStep('phone');
          showStudioChoice({
            status: 'context_required',
            contexts: s.contexts ?? [],
            selection_token: s.selection_token ?? '',
            expires_in: s.expires_in
          });
          return;
        }

        if (s.status === 'confirmed') {
          stopPoll();
          setCheckId(null);
          clearPendingCall('client');

          if (s.access_token) {
            await finishClientAuth({
              access_token: s.access_token,
              refresh_token: s.refresh_token ?? undefined
            });
            return;
          }

          if (s.registration_token) {
            setFormError('Пользователь с таким номером не найден. Обратитесь к администратору организации.');
            setCallStep('phone');
            return;
          }

          setFormError('Не удалось завершить вход. Попробуйте ещё раз.');
          setCallStep('phone');
          return;
        }

        // Неизвестный/необработанный статус не должен оставлять экран «в ожидании» без обратной связи.
        finish();
        setFormError('Не удалось завершить вход. Попробуйте снова или войдите по паролю.');
      } catch (e) {
        if (stopped) return;
        if (e instanceof ApiError) {
          if (e.status === 401) {
            finish();
            setFormError('Неверный логин или пароль.');
            return;
          }
          if (e.status === 404) {
            finish();
            setFormError('Сессия проверки недоступна. Запросите код снова.');
            return;
          }
          if (e.errorType === 'client_self_join_staff_only') {
            finish();
            setFormError('Студия принимает новых клиентов только через администратора');
            return;
          }
        }
        const message = e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Ошибка проверки звонка';
        finish();
        setFormError(message);
      }
    };

    intervalId = setInterval(() => {
      void tick();
    }, 1500);
    void tick();

    return () => {
      stopped = true;
      stopPoll();
    };
  }, [checkId, effectiveOrgSlug, isGlobalLogin, navigate, suffix, finishClientAuth, showStudioChoice]);

  useEffect(() => {
    if (callCooldown <= 0) return;
    const timer = setInterval(() => {
      setCallCooldown((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [callCooldown]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const switchTab = (tab: 'password' | 'call') => {
    setAuthTab(tab);
    setFormError(null);
    setNeedRegister(null);
  };

  const submitRequestCall = async (phoneNorm: string) => {
    setCallSubmitting(true);
    try {
      const res = await captcha.run((captchaToken) =>
        postRequestCall({
          phone: phoneNorm,
          org_slug: effectiveOrgSlug,
          ...(studioSlugs.length > 0 ? { org_slugs: studioSlugs } : {}),
          surface: 'client',
          register: true,
          call_mode: 'callcheck',
          ...(captchaToken ? { captcha_token: captchaToken } : {})
        })
      );
      setCallcheckPhonePretty(res.call_phone_pretty || res.call_phone || '');
      setCallStep('callcheck');
      setCheckId(res.check_id);
      savePendingCall({
        checkId: res.check_id,
        callPhonePretty: res.call_phone_pretty || res.call_phone || '',
        userPhone: callPhoneInput,
        orgSlug: effectiveOrgSlug,
        surface: 'client',
        createdAt: Date.now()
      });
      setCallCooldown(CALL_RESEND_COOLDOWN_S);
    } catch (err) {
      if (err instanceof ApiError) {
        setFormError(err.detail);
      } else {
        setFormError(err instanceof Error ? err.message : 'Ошибка запроса звонка');
      }
    } finally {
      setCallSubmitting(false);
      if (captcha.enabled) captcha.reset();
    }
  };

  const onCallSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setNeedRegister(null);
    if (!isGlobalLogin && !hasStudioScope) {
      setFormError('Организация не определена. Откройте виджет по ссылке студии.');
      return;
    }
    const phoneErr = validatePhoneRu(callPhoneInput, { required: true });
    if (phoneErr !== true) {
      setFormError(phoneErr);
      return;
    }
    const phoneNorm = normalizePhoneRu(callPhoneInput);
    lastPhoneNormRef.current = phoneNorm;
    await submitRequestCall(phoneNorm);
  };

// #__PLUSOFON_DISABLE__ — оригинальный код временно закомментирован, не удалён.
//   const onSwitchCallcheckOperator = async () => {
//     if (callSubmitting) return;
//     setFormError(null);
//     setNeedRegister(null);
//     const phoneNorm = lastPhoneNormRef.current || normalizePhoneRu(callPhoneInput);
//     if (!phoneNorm) return;
//     const nextProvider: 'sms_ru' | 'plusofon' = callProvider === 'sms_ru' ? 'plusofon' : 'sms_ru';
//     setCallSubmitting(true);
//     try {
//       const res = await postRequestCall({
//         phone: phoneNorm,
//         org_slug: effectiveOrgSlug,
//         register: true,
//         call_mode: 'callcheck',
//         provider: nextProvider
//       });
//       const phoneToCall = res.call_phone_pretty || res.call_phone || '';
//       if (!phoneToCall || !/\d/.test(phoneToCall)) {
//         setFormError('Не удалось получить номер для звонка');
//         return;
//       }
//       setCheckId(null);
//       setCallcheckPhonePretty(phoneToCall);
//       setCallProvider(nextProvider);
//       setCallcheckProviders(normalizeCallcheckProviders(res.callcheck_providers));
//       setCheckId(res.check_id);
//     } catch (err) {
//       if (err instanceof ApiError) {
//         setFormError(err.detail);
//       } else {
//         setFormError(err instanceof Error ? err.message : 'Ошибка смены оператора');
//       }
//     } finally {
//       setCallSubmitting(false);
//     }
//   };

  async function onPasswordSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setFormError(null);
    setNeedRegister(null);
    if (!isGlobalLogin && !hasStudioScope) {
      setFormError('Организация не определена. Откройте виджет по ссылке студии.');
      return;
    }
    const email = loginEmail.trim().toLowerCase();
    // Правило auth-methods-uniform §8: вход по паролю — email + телефон + пароль (3 поля).
    const errs: { email?: string; phone?: string; password?: string } = {};
    const emailErr = validateEmail(loginEmail, { required: true });
    if (emailErr !== true) errs.email = emailErr;
    const phoneErr = validatePhoneRu(accountPhone, { required: true });
    if (phoneErr !== true) errs.phone = phoneErr;
    if (!password.trim()) errs.password = 'Укажите пароль.';
    if (errs.email || errs.phone || errs.password) {
      setFieldErrors(errs);
      return;
    }
    setFieldErrors({});
    const phoneNorm = normalizePhoneRu(accountPhone);
    setPasswordLoading(true);
    try {
      const res = await captcha.run((captchaToken) =>
        postClientLogin({
          email,
          phone: phoneNorm,
          password,
          surface: 'client',
          ...(effectiveOrgSlug ? { org_slug: effectiveOrgSlug } : {}),
          ...(studioSlugs.length > 0 ? { org_slugs: studioSlugs } : {}),
          ...(captchaToken ? { captcha_token: captchaToken } : {})
        })
      );
      if (res.status === 'context_required' && Array.isArray(res.contexts) && res.selection_token) {
        showStudioChoice({
          status: 'context_required',
          contexts: res.contexts,
          selection_token: res.selection_token,
          expires_in: res.expires_in
        });
        return;
      }
      if (res.need_register || !res.access_token) {
        setFormError(
          res.message?.trim() ||
            'Пользователь не найден. Проверьте правильность введённых данных или обратитесь к администратору.'
        );
        return;
      }
      await finishClientAuth(
        { access_token: res.access_token, refresh_token: res.refresh_token ?? undefined },
        {
          organizationId: res.organization_id,
          organizationSlug: res.organization_slug,
          needOrgSelect: res.need_org_select
        }
      );
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.errorType === 'phone_not_verified') {
          setFormError('Сначала войдите по номеру телефона, чтобы подтвердить его.');
          return;
        }
        if (e.errorType === 'email_not_verified') {
          // Гейт email: показываем форму повторной отправки письма.
          setEmailNotVerified(true);
          setResendNote(e.detail || 'Необходимо подтвердить email.');
          setFormError(null);
          return;
        }
        if (e.errorType === 'client_self_join_staff_only') {
          setFormError('Студия принимает новых клиентов только через администратора');
          return;
        }
        setFormError(e.detail);
      } else {
        setFormError(e instanceof Error ? e.message : 'Ошибка входа');
      }
    } finally {
      setPasswordLoading(false);
      if (captcha.enabled) captcha.reset();
    }
  }

  async function onResendVerificationEmail(): Promise<void> {
    if (resendBusy || resendCooldown > 0) return;
    const email = loginEmail.trim().toLowerCase();
    if (validateEmail(email, { required: true }) !== true) {
      setResendNote('Укажите корректный email.');
      return;
    }
    setResendBusy(true);
    try {
      await postPublicResendEmailVerification(email);
      setResendNote('Письмо отправлено. Проверьте почту и подтвердите email.');
      setResendCooldown(RESEND_COOLDOWN_S);
    } catch (e) {
      if (e instanceof ApiError && e.status === 429) {
        setResendNote('Слишком частые попытки. Попробуйте позже.');
        setResendCooldown(RESEND_COOLDOWN_S);
      } else {
        setResendNote(e instanceof Error ? e.message : 'Не удалось отправить письмо.');
      }
    } finally {
      setResendBusy(false);
    }
  }

  if (bootstrapHint && !orgSlug && !embedGlobal) {
    return (
      <div className="mx-auto w-full max-w-md space-y-3 w-full min-w-0">
        <h1 className="text-xl font-semibold tracking-tight">Вход</h1>
        <p className="text-sm leading-relaxed text-[var(--text-muted)]">{bootstrapHint}</p>
      </div>
    );
  }

  if (studioChoice) {
    return (
      <div className="mx-auto w-full max-w-md space-y-4 w-full min-w-0">
        <h1 className="text-xl font-semibold tracking-tight">Вход</h1>
        <ContextSelectScreen
          title="Выберите студию"
          subtitle="Куда вы придёте?"
          contexts={studioChoice.contexts}
          variant="studio"
          busyId={studioBusyId}
          error={studioError}
          onSelect={(context) => void onSelectStudio(context)}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-md space-y-4 w-full min-w-0">
      <h1 className="text-xl font-semibold tracking-tight">Вход</h1>
      <PromoBanner screen="auth" />
      {searchParams.get('role_changed') === '1' ? (
        <EmbedMessageBlock variant="error" size="field" className="w-full" role="alert" aria-live="assertive">
          Ваша роль изменилась, войдите заново.
        </EmbedMessageBlock>
      ) : null}
      {searchParams.get('password_changed') === '1' ? (
        <EmbedMessageBlock variant="success" size="field" className="w-full" role="status" aria-live="polite">
          Пароль изменён. Войдите снова, указав новый пароль.
        </EmbedMessageBlock>
      ) : null}
      {searchParams.get('account_deleted') === '1' ? (
        <EmbedMessageBlock variant="success" size="field" className="w-full" role="status" aria-live="polite">
          Аккаунт удалён, данные обезличены. История записей у организации сохранена.
        </EmbedMessageBlock>
      ) : null}
      {isGlobalLogin ? (
        <p className="rounded-xl bg-[var(--bg-soft)] p-3 text-sm text-[var(--text-muted)]">
          Войдите — после этого выберите студию из списка ваших организаций.
        </p>
      ) : null}

      <div className="grid grid-cols-2 gap-1.5 sm:gap-2" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={authTab === 'call'}
          className={tabBtnClass(authTab === 'call')}
          onClick={() => {
            switchTab('call');
            setCallStep('phone');
            setFormError(null);
            setNeedRegister(null);
          }}
        >
          По звонку
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={authTab === 'password'}
          className={tabBtnClass(authTab === 'password')}
          onClick={() => switchTab('password')}
        >
          По паролю
        </button>
      </div>

      {authTab === 'password' ? (
        <form className="space-y-4 sm:space-y-3" onSubmit={(e) => void onPasswordSubmit(e)} noValidate>
          <div>
            <label className="mb-1.5 block text-[13px] font-medium text-[var(--text)]" htmlFor="embed-login-email">
              Логин (email) *
            </label>
            <input
              id="embed-login-email"
              type="email"
              autoComplete="username"
              placeholder="user@example.com"
              aria-invalid={fieldErrors.email ? true : undefined}
              className={`${inputClass}${fieldErrors.email ? ` ${embedFieldErrorInputClass}` : ''}`}
              value={loginEmail}
              onChange={(e) => {
                setLoginEmail(e.target.value);
                setFieldErrors((p) => ({ ...p, email: undefined }));
              }}
            />
            {fieldErrors.email ? <p className="mt-1 text-xs text-[var(--error-text)]">{fieldErrors.email}</p> : null}
          </div>
          <div>
            <label className="mb-1.5 block text-[13px] font-medium text-[var(--text)]" htmlFor="embed-account-phone">
              Телефон *
            </label>
            <EmbedPhoneInput
              id="embed-account-phone"
              className={`${inputClass}${fieldErrors.phone ? ` ${embedFieldErrorInputClass}` : ''}`}
              value={accountPhone}
              onChange={(v) => {
                setAccountPhone(v);
                setFieldErrors((p) => ({ ...p, phone: undefined }));
              }}
            />
            {fieldErrors.phone ? (
              <p className="mt-1 text-xs text-[var(--error-text)]">{fieldErrors.phone}</p>
            ) : (
              <p className="mt-1.5 text-xs leading-snug text-[var(--text-muted)]">
                Номер должен совпадать с телефоном учётной записи (как при регистрации).
              </p>
            )}
          </div>
          <div>
            <label className="mb-1.5 block text-[13px] font-medium text-[var(--text)]" htmlFor="embed-password">
              Пароль *
            </label>
            <div className="relative">
              <input
                id="embed-password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                aria-invalid={fieldErrors.password ? true : undefined}
                className={`${inputClass} pr-11 sm:pr-10${fieldErrors.password ? ` ${embedFieldErrorInputClass}` : ''}`}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setFieldErrors((p) => ({ ...p, password: undefined }));
                }}
              />
              <button
                type="button"
                className="absolute right-1 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-sm text-[var(--text-muted)] hover:bg-[var(--bg-soft)] hover:text-[var(--text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
                aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                aria-pressed={showPassword}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setShowPassword((v) => !v)}
              >
                {showPassword ? <IconEyeOff /> : <IconEye />}
              </button>
            </div>
            <p className="mt-1.5 text-xs leading-snug text-[var(--text-muted)]">Пароль от аккаунта клиента.</p>
            {fieldErrors.password ? (
              <p className="mt-1 text-xs text-[var(--error-text)]">{fieldErrors.password}</p>
            ) : null}
          </div>
          {formError ? (
            <EmbedMessageBlock variant="error" size="field" className="w-full" role="alert" aria-live="assertive">
              {formError}
            </EmbedMessageBlock>
          ) : null}
          {emailNotVerified ? (
            <div className="space-y-2 rounded-xl bg-[var(--bg-soft)] p-3">
              <p className="text-sm text-[var(--text)]">
                Необходимо подтвердить email. Мы отправили письмо со ссылкой на{' '}
                <span className="font-medium">{loginEmail.trim().toLowerCase()}</span>. Проверьте почту, затем войдите по паролю.
              </p>
              {resendNote ? (
                <p className="text-xs text-[var(--text-muted)]" role="status" aria-live="polite">
                  {resendNote}
                </p>
              ) : null}
              <Button
                type="button"
                variant="secondary"
                className="w-full min-h-10 sm:min-h-8"
                disabled={resendBusy || resendCooldown > 0}
                onClick={() => void onResendVerificationEmail()}
              >
                {resendBusy
                  ? 'Отправляем…'
                  : resendCooldown > 0
                    ? `Отправить письмо повторно через ${resendCooldown} сек`
                    : 'Отправить письмо повторно'}
              </Button>
            </div>
          ) : null}
          {needRegister ? (
            <EmbedMessageBlock variant="info" size="field" className="w-full" role="status" aria-live="polite" showIcon={false}>
              {needRegister}
            </EmbedMessageBlock>
          ) : null}
          <p className="text-center text-sm">
            <Link
              to={`${PATH.forgotPassword}${suffix}`}
              className="text-[var(--accent)] underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
            >
              Забыли пароль?
            </Link>
          </p>
          <p className="m-0 text-xs text-[var(--muted)]">Для входа укажите email, телефон и пароль.</p>
          <SmartCaptchaNotice
            serverEnabled={captcha.serverEnabled}
            challengeVisible={captcha.challengeVisible}
            status={captcha.status}
            onRetry={captcha.refresh}
          />
          <Button type="submit" className="w-full min-h-11 sm:min-h-9" disabled={passwordLoading || captcha.loading}>
            {passwordLoading ? 'Входим…' : 'Войти'}
          </Button>
        </form>
      ) : (
        <div className="space-y-3">
          {callStep === 'phone' ? (
            <form className="space-y-3" onSubmit={onCallSubmit} noValidate>
              <div>
                <label className="mb-1 block text-[13px] font-medium text-[var(--text)]" htmlFor="embed-call-phone">
                  Телефон
                </label>
                <EmbedPhoneInput
                  id="embed-call-phone"
                  className={inputClass}
                  value={callPhoneInput}
                  onChange={setCallPhoneInput}
                />
              </div>
              {formError ? (
                <EmbedMessageBlock variant="error" size="field" className="w-full" role="alert" aria-live="assertive">
                  {formError}
                </EmbedMessageBlock>
              ) : null}
              {needRegister ? (
                <EmbedMessageBlock variant="info" size="field" className="w-full" role="status" showIcon={false}>
                  {needRegister}
                </EmbedMessageBlock>
              ) : null}
              <SmartCaptchaNotice
                serverEnabled={captcha.serverEnabled}
                challengeVisible={captcha.challengeVisible}
                status={captcha.status}
                onRetry={captcha.refresh}
              />
              <Button type="submit" className="w-full min-h-11 sm:min-h-9" disabled={!canSubmitCall || callSubmitting || captcha.loading}>
                {callSubmitting ? 'Запрашиваем…' : 'Позвонить для входа'}
              </Button>
            </form>
          ) : null}
          {callStep === 'callcheck' ? (
            <div className="space-y-3">
              <p className="text-sm text-[var(--text-muted)]">
                Позвоните на номер{' '}
                <a href={telHref(callcheckPhonePretty)} className="font-semibold text-[var(--text)] underline underline-offset-2">
                  {callcheckPhonePretty}
                </a>{' '}
                с вашего телефона <strong className="text-[var(--text)]">{callPhoneInput}</strong> — звонок будет сброшен, вход подтвердится автоматически.
              </p>
              <div className="rounded-xl border border-[var(--border)] bg-[var(--bg-soft)] p-4 text-center">
                <a
                  href={telHref(callcheckPhonePretty)}
                  className="text-2xl font-bold tracking-wider text-[var(--text)] hover:underline"
                >
                  {callcheckPhonePretty}
                </a>
                <p className="mt-1 mb-0 text-xs text-[var(--text-muted)]">
                  Оператор: SMS.ru
                </p>
                {/*
#__PLUSOFON_DISABLE__: Оператор: {callProvider === 'plusofon' ? 'Плюсофон' : 'SMS.ru'}
*/}
              </div>
              <p className="text-sm text-[var(--text-muted)]">
                Звонок бесплатный. Как только вы совершите вызов, он будет сброшен, и вход выполнится автоматически.
              </p>
              <p className="text-xs text-[var(--text-muted)]">
                Можно свернуть приложение или вернуться в него позже: ожидание звонка сохранится, и вход продолжится автоматически.
              </p>
              {formError ? (
                <EmbedMessageBlock variant="error" size="field" className="w-full" role="alert" aria-live="assertive">
                  {formError}
                </EmbedMessageBlock>
              ) : null}
              {/*
#__PLUSOFON_DISABLE__ — кнопка смены оператора:
{callcheckProviders.includes('sms_ru') && callcheckProviders.includes('plusofon') ? (
  <Button
    type="button"
    variant="secondary"
    className="w-full"
    disabled={callSubmitting}
    onClick={() => void onSwitchCallcheckOperator()}
  >
    {callSubmitting ? 'Сменяем оператора…' : 'Сменить оператора'}
  </Button>
) : null}
*/}
              <Button
                type="button"
                variant="secondary"
                className="w-full"
                onClick={() => {
                  setCallStep('phone');
                  setCheckId(null);
                  clearPendingCall('client');
                  setCallcheckPhonePretty('');
                  setFormError(null);
                  setNeedRegister(null);
                  setCallCooldown(0);
                }}
              >
                Изменить номер
              </Button>
              {/*
#__PLUSOFON_DISABLE__ — прежняя кнопка «Отмена» заменена на «Изменить номер»:
<Button
  type="button"
  variant="secondary"
  className="w-full"
  onClick={() => {
    setCallStep('phone');
    setCheckId(null);
    setCallcheckPhonePretty('');
    setCallProvider('sms_ru');
    setFormError(null);
    setNeedRegister(null);
    setCallCooldown(0);
  }}
>
  Отмена
</Button>
*/}
            </div>
          ) : null}
        </div>
      )}

      {offerOverlay ? (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 p-4">
          <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-xl bg-[var(--bg)] shadow-xl">
            <div className="shrink-0 border-b border-[var(--border)] px-6 py-4 text-lg font-semibold text-[var(--text)]">
              {offerOverlay.title}
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4 text-sm leading-relaxed text-[var(--text)]">
              <LegalAgreementHtmlBlock html={offerOverlay.text} />
            </div>
            <div className="shrink-0 border-t border-[var(--border)] px-6 py-4 text-right">
              <Button type="button" variant="primary" onClick={() => setOfferOverlay(null)}>
                Закрыть
              </Button>
            </div>
          </div>
        </div>
      ) : null}

    </div>
  );
}
